import React, { useState, useEffect, useRef } from 'react';
import { adminAPI, plansAPI, ordersAPI, uploadAPI } from '../lib/api';
import { Package, ShoppingBag, CurrencyCircleDollar, Plus, Pencil, Trash, Download, Gear, Upload } from '@phosphor-icons/react';
import { toast } from 'sonner';
import PlanForm from '../components/admin/PlanForm';
import SettingsTab from '../components/admin/SettingsTab';
import TestimonialsTab from '../components/admin/TestimonialsTab';
import ContactsTab from '../components/admin/ContactsTab';
import { logger } from '../lib/logger';

const AdminPage = () => {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const bulkFileRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes, plansRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getAllOrders(),
        plansAPI.getPlans({})
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      logger.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlan = () => {
    setEditingPlan(null);
    setShowPlanForm(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setShowPlanForm(true);
  };

  const handleDeletePlan = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"? This cannot be undone.`)) return;
    try {
      await plansAPI.deletePlan(plan.plan_id);
      toast.success('Plan deleted');
      fetchData();
    } catch (err) {
      toast.error(`Delete failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleFormSaved = () => {
    setShowPlanForm(false);
    setEditingPlan(null);
    fetchData();
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkImporting(true);
    try {
      const response = await uploadAPI.bulkImport(file);
      const { created, errors } = response.data;
      if (created > 0) {
        toast.success(`Imported ${created} plans successfully`);
      }
      if (errors && errors.length > 0) {
        toast.error(`${errors.length} row(s) had errors. First: ${errors[0]}`);
      }
      fetchData();
    } catch (err) {
      toast.error(`Import failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBulkImporting(false);
      e.target.value = '';
    }
  };

  const downloadSampleCSV = () => {
    const csv = 'name,description,area_sqm,bedrooms,bathrooms,floors,height_m,plan_type,base_price,image_url\n"Modern 3-Bedroom","Open plan modern design",120,3,2,1,3.5,B,3500,https://example.com/plan.jpg\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plans-sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const [exportingCsv, setExportingCsv] = React.useState(false);
  const handleExportPlansCSV = async () => {
    if (exportingCsv) return;
    setExportingCsv(true);
    try {
      const res = await adminAPI.exportPlansCSV();
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `uds_plans_${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Plans exported');
    } catch (err) {
      logger.error('Plans export failed:', err);
      toast.error('Export failed');
    } finally {
      setExportingCsv(false);
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      const response = await ordersAPI.downloadInvoice(orderId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download invoice');
    }
  };

  const handleFulfillOrder = async (orderId) => {
    if (!window.confirm(`Manually mark order ${orderId} as paid and send files? Use this only for testing.`)) return;
    try {
      await ordersAPI.manualFulfill(orderId);
      toast.success('Order fulfilled - email sent');
      fetchData();
    } catch (err) {
      toast.error(`Fulfill failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      paid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Paid' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cancelled' },
      processing: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Processing' }
    };
    const { bg, text, label } = config[status] || config.pending;
    return <span className={`px-2 py-1 rounded text-xs font-medium ${bg} ${text}`}>{label}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12" data-testid="admin-page">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-black text-primary" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Admin Dashboard
          </h1>
        </div>

        {/* Tabs */}
        <div className="mb-8 border-b border-border overflow-x-auto">
          <nav className="flex space-x-8 min-w-max">
            {['overview', 'orders', 'plans', 'testimonials', 'contacts', 'settings'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 px-2 font-medium transition-colors capitalize ${
                  activeTab === tab
                    ? 'text-accent border-b-2 border-accent'
                    : 'text-text_muted hover:text-primary'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview */}
        {activeTab === 'overview' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <Package size={40} weight="duotone" className="text-accent" />
                  <div className="text-right">
                    <div className="text-3xl font-black text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{stats.total_plans}</div>
                    <div className="text-sm text-text_muted">Total Plans</div>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <ShoppingBag size={40} weight="duotone" className="text-accent" />
                  <div className="text-right">
                    <div className="text-3xl font-black text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{stats.total_orders}</div>
                    <div className="text-sm text-text_muted">Total Orders</div>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <CurrencyCircleDollar size={40} weight="duotone" className="text-accent" />
                  <div className="text-right">
                    <div className="text-3xl font-black text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>R{stats.total_revenue.toFixed(0)}</div>
                    <div className="text-sm text-text_muted">Total Revenue</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-border p-6">
              <h3 className="text-xl font-bold text-primary mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Recent Orders</h3>
              {orders.slice(0, 5).map(order => (
                <div key={order.order_id} className="py-3 border-b border-border last:border-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-mono text-sm text-text_muted">{order.order_id}</div>
                      <div className="text-sm">{order.user_name} • {order.items.length} item(s)</div>
                    </div>
                    <div className="text-right flex items-center space-x-3">
                      <div>
                        <div className="font-bold text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>R{order.total_amount.toFixed(2)}</div>
                        {getStatusBadge(order.status)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orders */}
        {activeTab === 'orders' && (
          <div className="bg-white border border-border p-6">
            <h3 className="text-xl font-bold text-primary mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>All Orders</h3>
            <div className="space-y-4">
              {orders.length === 0 ? (
                <p className="text-text_muted text-center py-8">No orders yet.</p>
              ) : orders.map(order => (
                <div key={order.order_id} className="p-4 border border-border" data-testid={`admin-order-${order.order_id}`}>
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-3 gap-3">
                    <div>
                      <div className="font-mono text-sm text-text_muted mb-1">{order.order_id}</div>
                      <div className="font-medium">{order.user_name}</div>
                      <div className="text-sm text-text_muted">{order.user_email}</div>
                      <div className="text-xs text-text_muted mt-1">{new Date(order.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <div className="text-xl font-bold text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>R{order.total_amount.toFixed(2)}</div>
                      {getStatusBadge(order.status)}
                    </div>
                  </div>
                  <div className="text-sm text-text_muted mb-3">
                    {order.items.length} item(s): {order.items.map(i => i.plan_name).join(', ')}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleDownloadInvoice(order.order_id)}
                      className="flex items-center space-x-1 text-xs px-3 py-1 border border-border rounded hover:bg-gray-50"
                      data-testid={`download-invoice-${order.order_id}`}
                    >
                      <Download size={14} />
                      <span>Invoice PDF</span>
                    </button>
                    {order.status !== 'paid' && (
                      <button
                        onClick={() => handleFulfillOrder(order.order_id)}
                        className="text-xs px-3 py-1 bg-primary text-white rounded hover:bg-opacity-90"
                        data-testid={`fulfill-order-${order.order_id}`}
                      >
                        Mark Paid & Send Files
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Plans */}
        {activeTab === 'plans' && (
          <div className="bg-white border border-border p-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-3">
              <h3 className="text-xl font-bold text-primary" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Manage Plans</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={downloadSampleCSV}
                  className="flex items-center space-x-2 px-3 py-2 border border-border rounded text-sm font-medium hover:bg-gray-50"
                  data-testid="download-sample-csv-btn"
                >
                  <Download size={16} />
                  <span>Sample CSV</span>
                </button>
                <button
                  onClick={handleExportPlansCSV}
                  disabled={exportingCsv || plans.length === 0}
                  className="flex items-center space-x-2 px-3 py-2 border border-primary text-primary rounded text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                  data-testid="export-plans-csv-btn"
                >
                  <Download size={16} />
                  <span>{exportingCsv ? 'Exporting...' : 'Download CSV'}</span>
                </button>
                <label className="flex items-center space-x-2 px-3 py-2 border border-primary text-primary rounded text-sm font-medium hover:bg-gray-50 cursor-pointer" data-testid="bulk-import-btn">
                  <Upload size={16} />
                  <span>{bulkImporting ? 'Importing...' : 'Bulk Import CSV'}</span>
                  <input
                    ref={bulkFileRef}
                    type="file"
                    accept=".csv"
                    onChange={handleBulkImport}
                    disabled={bulkImporting}
                    className="hidden"
                  />
                </label>
                <button
                  onClick={handleAddPlan}
                  className="flex items-center space-x-2 bg-accent text-white px-4 py-2 rounded font-medium hover:bg-opacity-90"
                  data-testid="add-plan-btn"
                >
                  <Plus size={20} weight="bold" />
                  <span>Add Plan</span>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.length === 0 ? (
                <p className="text-text_muted col-span-full text-center py-8">No plans yet. Click "Add Plan" to create your first.</p>
              ) : plans.map(plan => (
                <div key={plan.plan_id} className="border border-border overflow-hidden" data-testid={`admin-plan-${plan.plan_id}`}>
                  <div className="aspect-video overflow-hidden bg-gray-100">
                    {plan.image_url && <img src={plan.image_url} alt={plan.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-4">
                    <div className="font-bold text-primary mb-1">{plan.name}</div>
                    <div className="text-xs text-text_muted mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {plan.bedrooms} bed • {plan.floors} floor • {plan.area_sqm}m² • Type {plan.plan_type}
                    </div>
                    <div className="text-accent font-bold mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>R{plan.base_price.toFixed(2)}</div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditPlan(plan)}
                        className="flex-1 flex items-center justify-center space-x-1 py-2 border border-border text-xs hover:bg-gray-50"
                        data-testid={`edit-plan-${plan.plan_id}`}
                      >
                        <Pencil size={14} />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => handleDeletePlan(plan)}
                        className="flex items-center justify-center px-3 py-2 border border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`delete-plan-${plan.plan_id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Testimonials */}
        {activeTab === 'testimonials' && <TestimonialsTab />}

        {/* Contacts */}
        {activeTab === 'contacts' && <ContactsTab />}

        {/* Settings */}
        {activeTab === 'settings' && <SettingsTab />}
      </div>

      {/* Plan Form Modal */}
      {showPlanForm && (
        <PlanForm
          existingPlan={editingPlan}
          onSaved={handleFormSaved}
          onCancel={() => { setShowPlanForm(false); setEditingPlan(null); }}
        />
      )}
    </div>
  );
};

export default AdminPage;
